import * as Yup from "yup";

import {
    Box,
    Button,
    Container,
    Grid,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";

import { Close } from "@mui/icons-material";
import { createLecture } from "../../stores/lectures/lecturesSlice";
import { selectAllAreas } from "../../stores/areas/areasSelectors";
import { selectAllInstructors } from "../../stores/instructors/instructorsSelectors";
import { selectAllTopics } from "../../stores/topics/topicsSelectors";
import { selectUser } from "../../stores/auth/authSelectors";
import { useDispatch } from "../../stores";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const LectureCreate = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const user = useSelector(selectUser)

    const areas = useSelector(selectAllAreas);
    const instructors = useSelector(selectAllInstructors);
    const topics = useSelector(selectAllTopics);

    useEffect(() => {
        if (!user.user) {
            navigate("/login");
        }
    }, [user, navigate]);

    const formik = useFormik({
        initialValues: {
            title: "",
            description: "",
            image: null,
            topic: "",
            instructor: "",
            start_time: "",
            end_time: "",
            date: "",
            area: "",
            address_line1: "",
            address_line2: "",
        },
        validationSchema: Yup.object({
            title: Yup.string().required("Title is required"),
            description: Yup.string(),
            image: Yup.mixed().nullable(),
            topic: Yup.string().required("Topic is required"),
            instructor: Yup.string().required("Instructor is required"),
            start_time: Yup.string().required("Start time is required"),
            end_time: Yup.string().required("End time is required"),
            date: Yup.string().required("Date is required"),
            area: Yup.string().required("Area is required"),
            address_line1: Yup.string(),
            address_line2: Yup.string(),
        }),
        onSubmit: (values) => {
            const formattedValues = {
                ...values,
                topic: Number(values.topic),
                instructor: Number(values.instructor),
                area: Number(values.area),
            };

            dispatch(createLecture(formattedValues));
            navigate("/home");
        },
    });

    return (
        <Container maxWidth="sm" sx={{paddingTop:10}}>
            <Box display="flex" justifyContent="space-between">
                <Box onClick={() => navigate("/home")}>
                    <Close />
                </Box>

                <Typography variant="h5" textAlign="left" gutterBottom>
                    Create Lecture
                </Typography>
            </Box>

            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Title"
                            name="title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.title &&
                                Boolean(formik.errors.title)
                            }
                            helperText={
                                formik.touched.title && formik.errors.title
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.description &&
                                Boolean(formik.errors.description)
                            }
                            helperText={
                                formik.touched.description &&
                                formik.errors.description
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            select
                            label="Topic"
                            name="topic"
                            value={formik.values.topic}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.topic &&
                                Boolean(formik.errors.topic)
                            }
                            helperText={
                                formik.touched.topic && formik.errors.topic
                            }
                        >
                            {topics.map((topic) => (
                                <MenuItem key={topic.id} value={topic.id}>
                                    {topic.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            select
                            label="Instructor"
                            name="instructor"
                            value={formik.values.instructor}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.instructor &&
                                Boolean(formik.errors.instructor)
                            }
                            helperText={
                                formik.touched.instructor &&
                                formik.errors.instructor
                            }
                        >
                            {instructors.map((instructor) => (
                                <MenuItem
                                    key={instructor.id}
                                    value={instructor.id}
                                >
                                    {instructor.fullName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            select
                            label="Area"
                            name="area"
                            value={formik.values.area}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.area &&
                                Boolean(formik.errors.area)
                            }
                            helperText={
                                formik.touched.area && formik.errors.area
                            }
                        >
                            {areas.map((area) => (
                                <MenuItem key={area.id} value={area.id}>
                                    {area.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Start Time"
                            name="start_time"
                            type="time"
                            InputLabelProps={{ shrink: true }}
                            value={formik.values.start_time}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.start_time &&
                                Boolean(formik.errors.start_time)
                            }
                            helperText={
                                formik.touched.start_time &&
                                formik.errors.start_time
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="End Time"
                            name="end_time"
                            type="time"
                            InputLabelProps={{ shrink: true }}
                            value={formik.values.end_time}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.end_time &&
                                Boolean(formik.errors.end_time)
                            }
                            helperText={
                                formik.touched.end_time &&
                                formik.errors.end_time
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Date"
                            name="date"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={formik.values.date}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.date &&
                                Boolean(formik.errors.date)
                            }
                            helperText={
                                formik.touched.date && formik.errors.date
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address Line 1"
                            name="address_line1"
                            value={formik.values.address_line1}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.address_line1 &&
                                Boolean(formik.errors.address_line1)
                            }
                            helperText={
                                formik.touched.address_line1 &&
                                formik.errors.address_line1
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Address Line 2"
                            name="address_line2"
                            value={formik.values.address_line2}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={
                                formik.touched.address_line2 &&
                                Boolean(formik.errors.address_line2)
                            }
                            helperText={
                                formik.touched.address_line2 &&
                                formik.errors.address_line2
                            }
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            type="submit"
                        >
                            Submit
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
};

export default LectureCreate;
